import { z } from "zod/v4";

import { type SearchUserResult, SearchUserResultSchema } from "./users.types";

export const ALL_USERS = "AllUsers";
export const LOGGED_IN_USERS = "LoggedInUsers";

export type PermRelation = "reader" | "collaborator" | "owner";
export type NonOwnerPermRelation = Omit<PermRelation, "owner">;
export type DocPermission = "read" | "collaborate" | "share" | "own";
export type TablePermission = "read_all_rows" | "read_self_rows" | "write_rows";

export interface DocChangePermRequest {
	type: typeof ALL_USERS | typeof LOGGED_IN_USERS | "Username" | "Email" | "Workspace";
	// Optional for revoke — omitting relation means "revoke all access".
	relation?: PermRelation;
	value?: string;
}

export interface ShareType {
	// are we sharing publically or with a specific user?
	type: "grant" | "revoke";
	userType: "Username" | "Email";
	relation: NonOwnerPermRelation;
	user?: SearchUserResult;
	inviteEmail?: string;
}

export type PermissionUpdate =
	| {
			type: "Username";
			action: "grant" | "revoke";
			relation?: NonOwnerPermRelation;
			user: SearchUserResult; // required
			inviteEmail?: never;
	  }
	| {
			type: "Email";
			action: "grant" | "revoke";
			relation?: NonOwnerPermRelation;
			inviteEmail: string; // required
			user?: never;
	  }
	| {
			type: "Workspace";
			action: "grant" | "revoke";
			relation?: NonOwnerPermRelation;
			user?: never;
			inviteEmail?: never;
	  };

export interface UpdateUserPermission {
	user: SearchUserResult;
	type: "grant" | "revoke";
	relation: PermRelation;
}

export interface UpdateWorkspacePermission {
	type: "grant" | "revoke";
	relation: PermRelation;
}

export interface InvitationUpdate {
	email: string;
	type: "grant" | "revoke";
	relation: PermRelation;
}

export const PermissionInfoSchema = z.object({
	permission: z.enum(["read", "write", "share", "own"]),
	users: z.array(SearchUserResultSchema),
	appliedToEveryoneInWorkspace: z.boolean(),
});

export type PermissionInfo = z.infer<typeof PermissionInfoSchema>;

export const InviteResultSchema = z.object({
	email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
	referrer: z.string(),
	relation: z.enum(["reader", "collaborator", "owner", "revoke"]),
});

export type Invitation = z.infer<typeof InviteResultSchema>;
const UserWithPermissionSchema = SearchUserResultSchema.extend({
	email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
	permission: z.enum(["read", "collaborate", "own"]),
});

export type UserWithPermission = z.infer<typeof UserWithPermissionSchema>;

export const AuthZedSubject = z.object({
	id: z.string(),
	type: z.string(),
});

export type AuthZedSubjectSchema = z.Infer<typeof AuthZedSubject>;

export const PermissionsRolesResponseSchema = z.object({
	relation: z.object({
		// this order matters and it is prioritized by least to most privileged
		workspace: z.optional(z.array(AuthZedSubject)),
		reader: z.optional(z.array(AuthZedSubject)),
		collaborator: z.optional(z.array(AuthZedSubject)),
		owner: z.optional(z.array(AuthZedSubject)),
	}),
	users: z.record(z.string(), SearchUserResultSchema),
});

export type PermissionRolesResponse = z.infer<typeof PermissionsRolesResponseSchema>;

export type UserWithRelation = SearchUserResult & {
	relation: PermRelation;
};

export type LocalUserRelation = SearchUserResult & {
	relation: PermRelation | NonOwnerPermRelation | "revoke";
};
